import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import {
  ChefHat,
  Clock,
  Check,
  RefreshCw,
  Bell,
  Utensils,
  Package,
  Truck,
  Volume2,
  VolumeX,
  Wifi,
  WifiOff,
  Settings,
  X,
  ChevronRight,
  Filter,
  Users,
  MapPin,
  AlertTriangle,
  Timer
} from 'lucide-react';

const ORDER_TYPE_CONFIG = {
  dine_in: { label: 'Mesa', icon: Utensils, color: 'bg-blue-500' },
  takeout: { label: 'Mostrador', icon: Package, color: 'bg-orange-500' },
  delivery: { label: 'Domicilio', icon: Truck, color: 'bg-purple-500' }
};

const ITEM_STATUS = {
  pending: { label: 'Pendiente', color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: '⏳' },
  preparing: { label: 'En preparación', color: 'text-blue-600', bgColor: 'bg-blue-50', icon: '🔥' },
  ready: { label: 'Terminado', color: 'text-green-600', bgColor: 'bg-green-50', icon: '✓' }
};

const KitchenMonitor = () => {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundUnlocked, setSoundUnlocked] = useState(false); // Browser audio policy
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ yellow_minutes: 5, red_minutes: 10 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [filterType, setFilterType] = useState('all');
  const [filterStation, setFilterStation] = useState('all'); // Kitchen station filter: all, caliente, fria
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Alarm state - tracks items that are alarming
  const [alarmingItems, setAlarmingItems] = useState(new Set());
  // Critical items - very overdue (>150% of prep time)
  const [criticalItems, setCriticalItems] = useState([]);
  const [showCriticalOverlay, setShowCriticalOverlay] = useState(false);
  const [dismissedCriticals, setDismissedCriticals] = useState(new Set());
  const alarmAudioRef = useRef(null);
  const alarmIntervalRef = useRef(null);

  // Detect iOS/iPad
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Unlock audio on first user interaction (required by browser policy, especially iOS)
  const unlockAudio = useCallback(async () => {
    if (soundUnlocked) return;
    try {
      // Create audio context
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContextClass();
      
      // iOS requires explicit resume after user gesture
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      // Wait a bit for iOS
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Play a clear test sound to confirm it works
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(1320, ctx.currentTime + 0.3);
      oscillator.type = 'sine';
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
      
      // Store context for later use
      audioContextRef.current = ctx;
      setSoundUnlocked(true);
      toast.success('🔊 ¡Sonido activado! Escucharás notificaciones cuando lleguen pedidos', { duration: 4000 });
      console.log('🔊 Audio desbloqueado - iOS:', isIOS);
    } catch (e) {
      console.error('Error unlocking audio:', e);
      toast.error('Error al activar el sonido. Intenta de nuevo.');
    }
  }, [soundUnlocked, isIOS]);

  // Test sound function - for manual testing
  const testSound = useCallback(() => {
    if (!soundUnlocked) {
      unlockAudio();
      return;
    }
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContextClass();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1320, ctx.currentTime + 0.2);
      oscillator.type = 'sine';
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.4);
      toast.success('🔔 ¡Sonido de prueba!');
    } catch (e) {
      console.error('Test sound error:', e);
      toast.error('Error en sonido de prueba');
    }
  }, [soundUnlocked, unlockAudio]);

  // Update current time every second for live timers
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Alarm sound effect - plays repeatedly until stopped (iOS compatible)
  const playAlarmSound = useCallback(() => {
    if (!soundEnabled || !soundUnlocked) return;
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContextClass();
      
      // Resume if suspended (iOS)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Urgent alarm pattern - alternating frequencies
      oscillator.type = 'square';
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1200, ctx.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, ctx.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(1200, ctx.currentTime + 0.3);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.4);
      
      // Close context after sound finishes
      setTimeout(() => ctx.close(), 500);
    } catch (e) { console.log('Alarm sound error:', e); }
  }, [soundEnabled, soundUnlocked]);

  // Check for items exceeding their prep time and trigger alarms
  // Also check for orders waiting too long in pending status
  useEffect(() => {
    const checkAlarms = () => {
      const newAlarmingItems = new Set();
      const newCriticalItems = [];
      
      orders.forEach(order => {
        // Check for orders waiting too long in PENDING status (not started yet)
        if (order.status === 'pending' && order.created_at) {
          const createdAt = new Date(order.created_at);
          const elapsedMs = currentTime - createdAt;
          const elapsedMinutes = elapsedMs / 60000;
          const orderKey = `order-wait-${order.order_id}`;
          
          // Alert when order has been waiting longer than yellow_minutes (default 5)
          if (elapsedMinutes >= alertConfig.yellow_minutes) {
            newAlarmingItems.add(orderKey);
          }
          
          // Critical when order has been waiting longer than red_minutes (default 10)
          if (elapsedMinutes >= alertConfig.red_minutes && !dismissedCriticals.has(orderKey)) {
            newCriticalItems.push({
              key: orderKey,
              orderId: order.order_id,
              orderNumber: order.order_number,
              itemIndex: -1, // -1 indicates it's an order-level alert, not item-level
              productName: `⏳ PEDIDO EN ESPERA`,
              quantity: order.items?.length || 0,
              prepTime: alertConfig.red_minutes,
              elapsedMinutes: elapsedMinutes,
              overdueMinutes: Math.floor(elapsedMinutes - alertConfig.red_minutes),
              tableNumber: order.table_number,
              orderType: order.order_type,
              customerName: order.customer_name,
              isWaitingOrder: true // Flag to identify waiting orders
            });
          }
        }
        
        // Check items that are being prepared (existing logic)
        order.items?.forEach((item, idx) => {
          // Only check items that are being prepared (not ready/pending)
          if (item.item_status === 'preparing' && item.prep_time_minutes > 0 && item.item_started_at) {
            const startedAt = new Date(item.item_started_at);
            const elapsedMs = currentTime - startedAt;
            const elapsedMinutes = elapsedMs / 60000;
            const prepTime = item.prep_time_minutes;
            const itemKey = `${order.order_id}-${idx}`;
            
            // Start alarm when 90% of prep time has passed
            if (elapsedMinutes >= prepTime * 0.9) {
              newAlarmingItems.add(itemKey);
            }
            
            // Critical alert when 150% of prep time has passed (very overdue)
            if (elapsedMinutes >= prepTime * 1.5 && !dismissedCriticals.has(itemKey)) {
              newCriticalItems.push({
                key: itemKey,
                orderId: order.order_id,
                orderNumber: order.order_number,
                itemIndex: idx,
                productName: item.product_name,
                quantity: item.quantity,
                prepTime: prepTime,
                elapsedMinutes: elapsedMinutes,
                overdueMinutes: Math.floor(elapsedMinutes - prepTime),
                tableNumber: order.table_number,
                orderType: order.order_type,
                customerName: order.customer_name
              });
            }
          }
        });
      });
      
      setAlarmingItems(newAlarmingItems);
      setCriticalItems(newCriticalItems);
      
      // Auto-show overlay if there are critical items
      if (newCriticalItems.length > 0) {
        setShowCriticalOverlay(true);
      }
    };
    
    checkAlarms();
  }, [orders, currentTime, dismissedCriticals, alertConfig.yellow_minutes, alertConfig.red_minutes]);

  // Play alarm sound when there are alarming items
  useEffect(() => {
    if (alarmingItems.size > 0 && soundEnabled && soundUnlocked) {
      // Play alarm every 2 seconds
      if (!alarmIntervalRef.current) {
        playAlarmSound();
        alarmIntervalRef.current = setInterval(playAlarmSound, 2000);
      }
    } else {
      // Stop alarm
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
        alarmIntervalRef.current = null;
      }
    }
    
    return () => {
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
      }
    };
  }, [alarmingItems.size, soundEnabled, soundUnlocked, playAlarmSound]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    }
  }, []);

  // Track if sound is unlocked for refs
  const soundUnlockedRef = useRef(soundUnlocked);
  useEffect(() => {
    soundUnlockedRef.current = soundUnlocked;
  }, [soundUnlocked]);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabledRef.current || !soundUnlockedRef.current) {
      console.log('🔇 Sound blocked - enabled:', soundEnabledRef.current, 'unlocked:', soundUnlockedRef.current);
      return;
    }
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContextClass();
      
      // Resume if suspended (iOS requirement)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      
      // Pleasant notification melody
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(1320, ctx.currentTime + 0.3);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.5);
      
      // Close context after sound finishes
      setTimeout(() => ctx.close(), 600);
      console.log('🔔 Notification sound played');
    } catch (e) { console.log('Audio not supported:', e); }
  }, []);

  // Track previous orders to detect new ones
  const prevOrdersRef = useRef(null); // null initially to distinguish first load
  const soundEnabledRef = useRef(soundEnabled);
  
  // Keep ref in sync with state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);
  
  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/orders?status=pending,preparing');
      const newOrders = response.data;
      
      // Check for new orders (not in previous list) - skip on first load
      if (prevOrdersRef.current !== null && soundEnabledRef.current) {
        const prevOrderIds = new Set(prevOrdersRef.current.map(o => o.order_id));
        const brandNewOrders = newOrders.filter(o => !prevOrderIds.has(o.order_id));
        
        if (brandNewOrders.length > 0) {
          console.log('🔔 New orders detected:', brandNewOrders.map(o => o.order_number));
          playNotificationSound();
          brandNewOrders.forEach(o => {
            toast.success(`🔔 Nueva orden #${o.order_number}!`, { duration: 5000 });
          });
        }
      }
      
      prevOrdersRef.current = newOrders;
      setOrders(newOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [api, playNotificationSound]);

  const fetchAlertConfig = useCallback(async () => {
    try {
      const response = await api.get('/kitchen/alert-config');
      setAlertConfig(response.data);
    } catch (error) {
      console.error('Error fetching alert config:', error);
    }
  }, [api]);

  const saveAlertConfig = async () => {
    try {
      await api.put('/kitchen/alert-config', alertConfig);
      toast.success('Configuración guardada');
      setShowSettings(false);
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/kitchen/${token}`;
    try {
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => { setWsConnected(true); };
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'new_order') { 
            playNotificationSound(); 
            fetchOrders(); 
            toast.success(`Nueva orden #${message.data.order_number}!`); 
          }
        } catch (e) { console.error('Error parsing WebSocket message:', e); }
      };
      ws.onclose = () => { 
        setWsConnected(false); 
        wsRef.current = null; 
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000); 
      };
      ws.onerror = () => { setWsConnected(false); };
      wsRef.current = ws;
    } catch (e) { setWsConnected(false); }
  }, [playNotificationSound, fetchOrders]);

  useEffect(() => {
    fetchOrders();
    fetchAlertConfig();
    connectWebSocket();
    const pollInterval = setInterval(fetchOrders, 5000); // Check every 5 seconds for new orders
    return () => { 
      clearInterval(pollInterval); 
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchOrders, fetchAlertConfig, connectWebSocket]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status?status=${newStatus}`);
      toast.success(newStatus === 'ready' ? '¡Comanda lista!' : 'Estado actualizado');
      // Update local state instead of full refetch to prevent scroll reset
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.order_id === orderId 
            ? { ...order, status: newStatus }
            : order
        ).filter(order => !(order.order_id === orderId && newStatus === 'ready'))
      );
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const updateItemStatus = async (orderId, itemIndex, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/item/${itemIndex}/status?status=${newStatus}`);
      // Update local state instead of full refetch to prevent scroll reset
      setOrders(prevOrders => 
        prevOrders.map(order => {
          if (order.order_id === orderId) {
            const updatedItems = [...order.items];
            updatedItems[itemIndex] = { ...updatedItems[itemIndex], item_status: newStatus };
            return { ...order, items: updatedItems };
          }
          return order;
        })
      );
    } catch (error) {
      toast.error('Error al actualizar item');
    }
  };

  const prepareAllItems = async (order) => {
    try {
      for (let i = 0; i < order.items.length; i++) {
        const item = order.items[i];
        if (item.item_status === 'pending') {
          await api.put(`/orders/${order.order_id}/item/${i}/status?status=preparing`);
        }
      }
      toast.success('Preparando todos los items');
      // Update local state instead of full refetch
      setOrders(prevOrders => 
        prevOrders.map(o => {
          if (o.order_id === order.order_id) {
            const updatedItems = o.items.map(item => 
              item.item_status === 'pending' 
                ? { ...item, item_status: 'preparing' }
                : item
            );
            return { ...o, items: updatedItems };
          }
          return o;
        })
      );
    } catch (error) {
      toast.error('Error al preparar items');
    }
  };

  // Mark critical item as ready from overlay
  const markCriticalAsReady = async (item) => {
    try {
      await api.put(`/orders/${item.orderId}/item/${item.itemIndex}/status?status=ready`);
      toast.success(`¡${item.productName} marcado como listo!`);
      // Remove from dismissed list since it's now ready
      setDismissedCriticals(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.key);
        return newSet;
      });
      // Update local state instead of full refetch
      setOrders(prevOrders => 
        prevOrders.map(order => {
          if (order.order_id === item.orderId) {
            const updatedItems = [...order.items];
            updatedItems[item.itemIndex] = { ...updatedItems[item.itemIndex], item_status: 'ready' };
            return { ...order, items: updatedItems };
          }
          return order;
        })
      );
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Dismiss a critical item temporarily (will reappear if not resolved)
  const dismissCriticalItem = (itemKey) => {
    setDismissedCriticals(prev => new Set([...prev, itemKey]));
  };

  // Dismiss all and close overlay
  const dismissAllCriticals = () => {
    const keys = criticalItems.map(item => item.key);
    setDismissedCriticals(prev => new Set([...prev, ...keys]));
    setShowCriticalOverlay(false);
  };

  const getNextStatus = (currentStatus) => {
    if (currentStatus === 'pending') return 'preparing';
    if (currentStatus === 'preparing') return 'ready';
    return 'ready';
  };

  // Calculate elapsed time
  const getElapsedTime = (createdAt) => {
    const created = new Date(createdAt);
    const diffMs = currentTime - created;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate remaining prep time for an item
  const getPrepTimeRemaining = (item) => {
    if (!item.prep_time_minutes || item.prep_time_minutes <= 0) return null;
    if (item.item_status === 'ready') return null;
    
    const startTime = item.item_started_at 
      ? new Date(item.item_started_at) 
      : new Date(item.item_created_at);
    
    const elapsedMs = currentTime - startTime;
    const elapsedMinutes = elapsedMs / 60000;
    const remainingMinutes = item.prep_time_minutes - elapsedMinutes;
    
    return {
      remaining: remainingMinutes,
      totalMinutes: item.prep_time_minutes,
      percentage: Math.max(0, Math.min(100, (remainingMinutes / item.prep_time_minutes) * 100)),
      isOverdue: remainingMinutes <= 0,
      isWarning: remainingMinutes > 0 && remainingMinutes <= item.prep_time_minutes * 0.2 // Last 20%
    };
  };

  // Format remaining time as MM:SS
  const formatRemainingTime = (minutes) => {
    if (minutes <= 0) {
      const overdue = Math.abs(minutes);
      const mins = Math.floor(overdue);
      const secs = Math.floor((overdue - mins) * 60);
      return `-${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = (createdAt) => {
    const created = new Date(createdAt);
    const diffMins = Math.floor((currentTime - created) / 60000);
    if (diffMins >= alertConfig.red_minutes) return 'text-red-600 bg-red-100';
    if (diffMins >= alertConfig.yellow_minutes) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  // Filter orders by type and station
  const filteredOrders = orders.filter(o => {
    // Filter by order type
    if (filterType !== 'all' && o.order_type !== filterType) return false;
    
    // Filter by kitchen station - only show orders that have items for this station
    if (filterStation !== 'all') {
      const hasMatchingItem = o.items?.some(item => item.kitchen_station === filterStation);
      if (!hasMatchingItem) return false;
    }
    
    return true;
  });

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
    }
  };

  // Settings Modal
  const SettingsModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSettings(false)}>
      <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Configurar Alertas</h3>
          <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-2">Alerta Amarilla (minutos)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={alertConfig.yellow_minutes}
                onChange={(e) => setAlertConfig(prev => ({ ...prev, yellow_minutes: parseInt(e.target.value) || 0 }))}
                className="w-full border rounded px-3 py-2"
                min="1" max="60"
              />
              <div className="w-8 h-8 bg-yellow-400 rounded flex-shrink-0" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-2">Alerta Roja (minutos)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={alertConfig.red_minutes}
                onChange={(e) => setAlertConfig(prev => ({ ...prev, red_minutes: parseInt(e.target.value) || 0 }))}
                className="w-full border rounded px-3 py-2"
                min="1" max="60"
              />
              <div className="w-8 h-8 bg-red-500 rounded flex-shrink-0" />
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={() => setShowSettings(false)} className="flex-1">
            Cancelar
          </Button>
          <Button onClick={saveAlertConfig} className="flex-1 bg-orange-500 hover:bg-orange-600">
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );

  // Critical Items Full-Screen Overlay
  const CriticalOverlay = () => {
    if (!showCriticalOverlay || criticalItems.length === 0) return null;
    
    return (
      <div 
        className="fixed inset-0 z-[100] flex items-center justify-center"
        data-testid="critical-overlay"
      >
        {/* Animated red background */}
        <div className="absolute inset-0 bg-red-600 animate-pulse opacity-90" />
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-red-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-full animate-bounce">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">¡ATENCIÓN URGENTE!</h2>
                  <p className="text-red-100">{criticalItems.length} platillo{criticalItems.length > 1 ? 's' : ''} muy atrasado{criticalItems.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowCriticalOverlay(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          {/* Items List */}
          <div className="p-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-3">
              {criticalItems.map((item) => (
                <div 
                  key={item.key}
                  className="bg-red-50 border-2 border-red-300 rounded-xl p-4 animate-pulse"
                  data-testid={`critical-item-${item.orderNumber}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {/* Order Info */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                          #{item.orderNumber}
                        </span>
                        {item.orderType === 'dine_in' && item.tableNumber && (
                          <span className="bg-blue-100 text-blue-800 text-sm px-2 py-0.5 rounded">
                            Mesa {item.tableNumber}
                          </span>
                        )}
                        {item.customerName && (
                          <span className="text-gray-600 text-sm">{item.customerName}</span>
                        )}
                      </div>
                      
                      {/* Product Info */}
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-gray-800">
                          {item.quantity}x {item.productName}
                        </span>
                      </div>
                      
                      {/* Time Info */}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-gray-500">
                          Tiempo estimado: {item.prepTime} min
                        </span>
                        <span className="text-red-600 font-bold flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          ¡{item.overdueMinutes} min de retraso!
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => markCriticalAsReady(item)}
                        className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all transform hover:scale-105 shadow-lg"
                        data-testid={`critical-ready-${item.orderNumber}`}
                      >
                        <Check className="w-5 h-5" />
                        ¡LISTO!
                      </button>
                      <button
                        onClick={() => dismissCriticalItem(item.key)}
                        className="text-gray-500 hover:text-gray-700 text-sm underline"
                      >
                        Ignorar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-100 p-4 border-t">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Los platillos aparecen aquí cuando superan el 150% de su tiempo de preparación
              </p>
              <button
                onClick={dismissAllCriticals}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cerrar y continuar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Comanda Card Component (FUDO Style)
  const ComandaCard = ({ order, filterStation }) => {
    const typeConfig = ORDER_TYPE_CONFIG[order.order_type] || ORDER_TYPE_CONFIG.dine_in;
    // When filtering by station, check if items from that station are ready
    const relevantItems = filterStation !== 'all' 
      ? order.items?.filter(item => item.kitchen_station === filterStation)
      : order.items;
    const allItemsReady = relevantItems?.every(item => item.item_status === 'ready');
    const hasPendingItems = relevantItems?.some(item => item.item_status === 'pending');

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden w-80 flex-shrink-0" data-testid={`comanda-${order.order_number}`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-lg">#{order.order_number}</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTimeColor(order.created_at)}`}>
              {getElapsedTime(order.created_at)}
            </span>
          </div>
          
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <span className={`px-2 py-0.5 rounded text-xs text-white ${typeConfig.color}`}>
                {typeConfig.label}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatTime(order.created_at)}</span>
            </div>
          </div>

          {/* Table/Customer Info */}
          <div className="flex flex-col gap-1 mt-2 text-sm">
            <div className="flex items-center justify-between">
              {order.order_type === 'dine_in' && order.table_number && (
                <span className="font-medium">Mesa {order.table_number}</span>
              )}
              {order.customer_name && (
                <span className="text-gray-600">{order.customer_name}</span>
              )}
              {order.num_guests && (
                <span className="flex items-center gap-1 text-gray-500">
                  <Users className="w-4 h-4" />
                  {order.num_guests} Persona{order.num_guests > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {/* Show delivery address for delivery orders */}
            {order.order_type === 'delivery' && order.customer_address && (
              <div className="flex items-center gap-1 text-purple-600 bg-purple-50 px-2 py-1 rounded text-xs">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{order.customer_address}</span>
              </div>
            )}
            {/* Show general order notes */}
            {order.notes && (
              <div className="flex items-start gap-1 text-orange-700 bg-orange-100 px-2 py-1.5 rounded text-xs border border-orange-300">
                <span className="text-base leading-none">📝</span>
                <span className="font-medium break-words">{order.notes}</span>
              </div>
            )}
          </div>
        </div>

        {/* Items List */}
        <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto overscroll-contain touch-pan-y">
          {order.items?.map((item, idx) => {
            // Skip rendering items that don't match the station filter
            if (filterStation !== 'all' && item.kitchen_station !== filterStation) {
              return null;
            }
            
            const status = ITEM_STATUS[item.item_status || 'pending'];
            const isReady = item.item_status === 'ready';
            const isPreparing = item.item_status === 'preparing';
            const prepTimeInfo = getPrepTimeRemaining(item);
            const itemAlarmKey = `${order.order_id}-${idx}`;
            const isAlarming = alarmingItems.has(itemAlarmKey);
            const stationBadge = item.kitchen_station === 'caliente' 
              ? { icon: '🔥', label: 'Caliente', bg: 'bg-red-100 text-red-700' }
              : item.kitchen_station === 'fria' 
                ? { icon: '🧊', label: 'Fría', bg: 'bg-blue-100 text-blue-700' }
                : null;
            
            // Determine item background based on timer status
            let itemBgClass = 'bg-gray-50 border-gray-200';
            if (isReady) {
              itemBgClass = 'bg-green-50 border-green-200';
            } else if (prepTimeInfo?.isOverdue) {
              itemBgClass = 'bg-red-100 border-red-400 animate-pulse';
            } else if (prepTimeInfo?.isWarning) {
              itemBgClass = 'bg-yellow-100 border-yellow-400';
            }
            
            return (
              <div 
                key={idx} 
                className={`p-3 rounded-lg border ${itemBgClass}`}
                data-testid={`item-${order.order_number}-${idx}`}
              >
                {/* Product Name with Station Badge and Promo Badge */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {item.is_promo_item && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500 text-white font-bold animate-pulse">
                        🏷️ PROMO
                      </span>
                    )}
                    <span className={`font-medium ${isReady ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                      {item.quantity} {item.product_name}
                    </span>
                  </div>
                  {stationBadge && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${stationBadge.bg}`}>
                      {stationBadge.icon} {stationBadge.label}
                    </span>
                  )}
                </div>
                
                {/* Promo Details */}
                {item.is_promo_item && item.promo_name && (
                  <div className="text-xs text-purple-600 mb-1 font-medium">
                    Promoción: {item.promo_name}
                  </div>
                )}
                
                {/* Description - what's included in combo/promo */}
                {item.description && (
                  <div className="text-xs text-amber-600 mb-1">
                    📋 {item.description}
                  </div>
                )}
                
                {item.notes && (
                  <div className="text-xs text-orange-600 mb-2">Nota: {item.notes}</div>
                )}

                {/* Timer Display - Only show when item has prep time and is not ready */}
                {prepTimeInfo && !isReady && (
                  <div className="mb-2">
                    {/* Progress Bar */}
                    <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                      <div 
                        className={`absolute left-0 top-0 h-full transition-all duration-1000 ${
                          prepTimeInfo.isOverdue ? 'bg-red-500' : 
                          prepTimeInfo.isWarning ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${prepTimeInfo.percentage}%` }}
                      />
                    </div>
                    
                    {/* Timer Info */}
                    <div className="flex items-center justify-between text-xs">
                      <div className={`flex items-center gap-1 font-mono font-bold ${
                        prepTimeInfo.isOverdue ? 'text-red-600' : 
                        prepTimeInfo.isWarning ? 'text-yellow-700' : 'text-gray-600'
                      }`}>
                        {isAlarming && <AlertTriangle className="w-3 h-3 animate-bounce" />}
                        <Timer className="w-3 h-3" />
                        <span>{formatRemainingTime(prepTimeInfo.remaining)}</span>
                      </div>
                      <span className="text-gray-400">
                        / {item.prep_time_minutes} min
                      </span>
                    </div>
                  </div>
                )}

                {/* Status Row */}
                <div className="flex items-center justify-between">
                  <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${status?.bgColor || 'bg-gray-50'} ${status?.color || 'text-gray-600'}`}>
                    <span>{status?.icon || '⏳'}</span>
                    <span>{status?.label || 'Pendiente'}</span>
                  </div>
                  
                  {!isReady && (
                    <button
                      onClick={() => updateItemStatus(order.order_id, idx, getNextStatus(item.item_status || 'pending'))}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        isAlarming 
                          ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                      data-testid={`advance-item-${order.order_number}-${idx}`}
                    >
                      {isAlarming ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Listo</span>
                        </>
                      ) : (
                        <>
                          <ChevronRight className="w-4 h-4" />
                          <ChevronRight className="w-4 h-4 -ml-3" />
                        </>
                      )}
                    </button>
                  )}
                  
                  {isReady && (
                    <div className="flex items-center gap-1 text-green-600">
                      <Check className="w-5 h-5" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Button */}
        <div className="p-3 border-t border-gray-100">
          {allItemsReady ? (
            <button
              onClick={() => updateOrderStatus(order.order_id, 'ready')}
              className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid={`complete-order-${order.order_number}`}
            >
              <Check className="w-5 h-5" />
              Comanda Lista
            </button>
          ) : hasPendingItems ? (
            <button
              onClick={() => prepareAllItems(order)}
              className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-colors"
              data-testid={`prepare-all-${order.order_number}`}
            >
              Preparar todo
            </button>
          ) : (
            <div className="text-center text-sm text-gray-500 py-2">
              En preparación...
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center md:ml-64 bg-gray-100">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <main className="flex-1 md:ml-64 flex flex-col">
        {/* Sound Unlock Modal - Highest priority, shows over everything on iOS/iPad */}
        {!soundUnlocked && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div 
              onClick={unlockAudio}
              className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 rounded-3xl p-8 max-w-lg w-full cursor-pointer shadow-2xl transform hover:scale-105 transition-transform"
            >
              <div className="text-center text-white">
                <div className="flex justify-center mb-4">
                  <div className="bg-white/20 p-6 rounded-full animate-bounce">
                    <Volume2 className="w-16 h-16" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold mb-2">🔔 ACTIVA EL SONIDO</h2>
                <p className="text-xl mb-4">
                  {isIOS 
                    ? 'iPad/iPhone requiere tu permiso para reproducir audio'
                    : 'Toca para recibir alertas de pedidos nuevos'
                  }
                </p>
                <div className="bg-white/20 rounded-xl p-4 mb-4">
                  <p className="text-lg">
                    👆 <strong>TOCA EN CUALQUIER PARTE</strong> de esta ventana
                  </p>
                </div>
                <p className="text-sm opacity-80">
                  Escucharás un sonido de confirmación cuando se active
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Critical Items Overlay */}
        <CriticalOverlay />
        
        {/* Settings Modal */}
        {showSettings && <SettingsModal />}
        
        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-orange-500 text-white">
                <ChefHat className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Monitor de Cocina</h1>
                <p className="text-sm text-gray-500">
                  {filteredOrders.length} comanda{filteredOrders.length !== 1 ? 's' : ''} activa{filteredOrders.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Critical Items Button - Show overlay again */}
              {criticalItems.length > 0 && (
                <button
                  onClick={() => setShowCriticalOverlay(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-full animate-pulse shadow-lg"
                  data-testid="critical-alert-btn"
                >
                  <AlertTriangle className="w-5 h-5 animate-bounce" />
                  <span className="font-bold">{criticalItems.length} CRÍTICO{criticalItems.length > 1 ? 'S' : ''}</span>
                </button>
              )}
              
              {/* Alarm Indicator */}
              {alarmingItems.size > 0 && criticalItems.length === 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full animate-pulse" data-testid="alarm-indicator">
                  <AlertTriangle className="w-4 h-4 animate-bounce" />
                  <span className="font-bold text-sm">{alarmingItems.size} URGENTE{alarmingItems.size > 1 ? 'S' : ''}</span>
                </div>
              )}
              
              {/* Connection Status */}
              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${wsConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {wsConnected ? 'En vivo' : 'Offline'}
              </div>

              {/* Test Sound Button - shows after unlocking */}
              {soundUnlocked && (
                <Button 
                  variant="outline"
                  size="sm" 
                  onClick={testSound}
                  className="gap-1"
                  title="Probar sonido"
                >
                  <Bell className="w-4 h-4" />
                  Probar
                </Button>
              )}

              {/* Sound Toggle - Click to unlock audio */}
              <Button 
                variant={soundUnlocked ? "outline" : "default"}
                size="icon" 
                onClick={() => {
                  if (!soundUnlocked) {
                    unlockAudio();
                  } else {
                    setSoundEnabled(!soundEnabled);
                  }
                }}
                className={!soundUnlocked ? "bg-orange-500 hover:bg-orange-600 animate-pulse" : ""}
                title={!soundUnlocked ? "Clic para activar sonido" : (soundEnabled ? "Sonido activado" : "Sonido desactivado")}
              >
                {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </Button>

              {/* Notifications */}
              {!notificationsEnabled && (
                <Button variant="outline" size="icon" onClick={requestNotificationPermission}>
                  <Bell className="w-5 h-5" />
                </Button>
              )}

              {/* Settings */}
              <Button variant="outline" size="icon" onClick={() => setShowSettings(true)} data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>

              {/* Refresh */}
              <Button variant="outline" size="icon" onClick={fetchOrders}>
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Audio Unlock Banner - inline version */}
          {!soundUnlocked && !showCriticalOverlay && (
            <div 
              onClick={unlockAudio}
              className="mt-3 p-5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl cursor-pointer hover:from-orange-600 hover:to-red-600 transition-all shadow-lg"
            >
              <div className="flex items-center justify-center gap-4">
                <div className="bg-white/20 p-3 rounded-full animate-bounce">
                  <Volume2 className="w-8 h-8" />
                </div>
                <div className="text-center">
                  <p className="font-bold text-xl md:text-2xl">👆 TOCA AQUÍ PARA ACTIVAR SONIDO</p>
                  <p className="text-sm opacity-90 mt-1">
                    {isIOS ? '(Requerido en iPad/iPhone)' : 'Recibirás alertas cuando lleguen pedidos'}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-full animate-bounce">
                  <Bell className="w-8 h-8" />
                </div>
              </div>
            </div>
          )}

          {/* Filter Row - Order Type and Kitchen Station */}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            {/* Order Type Filters */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${filterType === 'all' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
              >
                Todas
              </button>
              <button
                onClick={() => setFilterType('dine_in')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${filterType === 'dine_in' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
              >
                Mesa
              </button>
              <button
                onClick={() => setFilterType('takeout')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${filterType === 'takeout' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
              >
                Mostrador
              </button>
              <button
                onClick={() => setFilterType('delivery')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${filterType === 'delivery' ? 'bg-white shadow' : 'hover:bg-gray-200'}`}
              >
                Domicilio
              </button>
            </div>

            {/* Kitchen Station Filters */}
            <div className="flex items-center gap-1 bg-orange-50 rounded-lg p-1 border border-orange-200">
              <span className="px-2 text-sm font-medium text-orange-700">Estación:</span>
              <button
                onClick={() => setFilterStation('all')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${filterStation === 'all' ? 'bg-orange-500 text-white shadow' : 'hover:bg-orange-100 text-orange-700'}`}
                data-testid="filter-station-all"
              >
                Todo
              </button>
              <button
                onClick={() => setFilterStation('caliente')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${filterStation === 'caliente' ? 'bg-red-500 text-white shadow' : 'hover:bg-red-100 text-red-700'}`}
                data-testid="filter-station-caliente"
              >
                🔥 Caliente
              </button>
              <button
                onClick={() => setFilterStation('fria')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1 ${filterStation === 'fria' ? 'bg-blue-500 text-white shadow' : 'hover:bg-blue-100 text-blue-700'}`}
                data-testid="filter-station-fria"
              >
                🧊 Fría
              </button>
            </div>
          </div>

          {/* Alert Legend */}
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-200 rounded" />
              <span>Normal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-400 rounded" />
              <span>{alertConfig.yellow_minutes}+ min</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded" />
              <span>{alertConfig.red_minutes}+ min</span>
            </div>
          </div>
        </div>

        {/* Main Content - Horizontal Scroll */}
        <div className="flex-1 p-6 overflow-x-auto">
          {filteredOrders.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <ChefHat className="w-20 h-20 mx-auto mb-4 opacity-30" />
                <p className="text-xl font-bold">Sin comandas</p>
                <p className="text-sm">Las nuevas órdenes aparecerán aquí</p>
              </div>
            </div>
          ) : (
            <div className="flex gap-4 pb-4">
              {filteredOrders.map(order => (
                <ComandaCard key={order.order_id} order={order} filterStation={filterStation} />
              ))}
            </div>
          )}
        </div>

        {/* Footer Stats */}
        <div className="bg-white border-t px-6 py-3">
          <div className="flex justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <Utensils className="w-4 h-4 text-blue-500" />
              <span>Mesa: <strong>{orders.filter(o => o.order_type === 'dine_in').length}</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-500" />
              <span>Mostrador: <strong>{orders.filter(o => o.order_type === 'takeout').length}</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-purple-500" />
              <span>Domicilio: <strong>{orders.filter(o => o.order_type === 'delivery').length}</strong></span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KitchenMonitor;
